import { createProjectMarkersApi } from '@core/core/api'
import { updateMultipartState, useAppDispatch } from '@core/core/store'
import type { MultipartFileStatusType, MultipartUploadedFile } from '@core/core/types'
import { addSimpleNotification } from '@notifications/actions'
import { nanoid } from '@reduxjs/toolkit'
import { getPluralFormatter, isObject } from 'common/helpers'
import type { NotificationStatus } from 'common/types'
import { useCallback, useEffect } from 'react'

import type { MarkerParseResult } from './types'

const connectProblems = getPluralFormatter('проблема')
const connectErrors = getPluralFormatter('ошибка')
const connectMarker = getPluralFormatter('маркер')
// FIXME согласование причастий не совсем правильное
const connectAdded = getPluralFormatter('добавлено')
const connectUpdated = getPluralFormatter('обновлено')
const connectSkipped = getPluralFormatter('пропущено')

export const useMarkerLoader = (
  uploadFile: MultipartUploadedFile | undefined,
  parsedMarkers: MarkerParseResult | undefined,
  removeOpenFiles: (fileIds: string[]) => void,
) => {
  const dispatch = useAppDispatch()

  const uploadId = uploadFile?.uploadId
  const notify = useCallback(
    (status: NotificationStatus, message: string) => {
      addSimpleNotification({
        id: nanoid(),
        taskId: uploadId ?? nanoid(),
        status,
        message,
        autoClose: 5,
      })
    },
    [uploadId],
  )

  const fileId = uploadFile?.fileId
  const statuses = uploadFile?.statuses
  const setStatus = useCallback(
    (status: MultipartFileStatusType, progress?: number, error?: string) => {
      if (!fileId) return

      dispatch(
        updateMultipartState({
          id: fileId,
          changes: {
            progress,
            statuses: { ...statuses, loading: status },
            error,
          },
        }),
      )
    },
    [fileId, statuses],
  )
  const setError = useCallback(
    (error: string) => {
      if (!fileId) return

      dispatch(
        updateMultipartState({
          id: fileId,
          changes: {
            statuses: { ...statuses, loading: 'failed' },
            error,
          },
        }),
      )
    },
    [fileId, statuses],
  )

  const projectId = uploadFile?.projectId
  const isLoading = uploadFile?.step === 'loading'
  const fileName = uploadFile?.name
  const updateIfExists = uploadFile?.updateIfExists ?? false
  useEffect(() => {
    if (isLoading && parsedMarkers && projectId) {
      if (parsedMarkers.errors.length !== 0) {
        const errorsToShow = 3
        parsedMarkers.errors.slice(0, errorsToShow).forEach(error => {
          const columnMessage =
            error.column !== undefined ? ` при получении значения колонки '${error.column}'` : ''
          notify(
            'alert',
            `При парсинге файла '${fileName}' в строке ${error.line}${columnMessage} произошла ошибка: ${error.error}.`,
          )
        })
        const more = Math.max(parsedMarkers.errors.length - errorsToShow, 0)
        if (more) {
          notify(
            'alert',
            `При парсинге файла '${fileName}' найдено еще ${more} ${connectErrors(more)}.`,
          )
        }

        setError(
          `При парсинге файла '${fileName}' найдено ${
            parsedMarkers.errors.length
          } ${connectProblems(parsedMarkers.errors.length)}`,
        )
      } else if (parsedMarkers.markers.length === 0) {
        notify('warning', 'Нет ни одного маркера для загрузки')
        setStatus('idle')
      } else {
        setStatus('inprogress', 50)
        createProjectMarkersApi(projectId, parsedMarkers.markers, updateIfExists)
          .then(results => {
            let message = `Из файла '${fileName}' успешно ${connectAdded(
              results.created,
            )} в проект ${results.created} ${connectMarker(results.created)}`
            if (results.updated) {
              message += ` и ${connectUpdated(results.updated)} ${results.updated} ${connectMarker(
                results.updated,
              )}`
            }
            if (results.skipped) {
              message += `. Уже существует в проекте и ${connectSkipped(results.skipped)} ${
                results.skipped
              } ${connectMarker(results.skipped)}`
            }
            message += '.'
            notify('success', message)
          })
          .then(() => {
            setStatus('succeeded', 100)
          })
          .then(() =>
            setTimeout(() => {
              removeOpenFiles([uploadFile.fileId])
            }, 1000),
          )
          .catch((reason: unknown) => {
            if (isObject(reason) && 'message' in reason && typeof reason.message === 'string') {
              const message = `Ошибка при сохранении маркеров: ${reason.message}`
              notify('alert', message)
              setError(message)
            }
          })
      }
    }
  }, [parsedMarkers])
}
